<ng-container>
	<form [formGroup]="invoiceForm" *ngIf="invoiceForm" (keydown.enter)="onEnterKeyDown($event)" class="no-overflow mt-3">
		<ng-template matStepLabel>Invoice Information</ng-template>
		<div class="container-fluid">
			<!-- Suggested GRNs matching the invoice lines (operator confirms each — never auto-added) -->
			<div class="row mb-2" *ngIf="extracted && suggestedBatches.length">
				<div class="col-12">
					<div class="ai-suggest-head">
						<span><mat-icon>auto_awesome</mat-icon> Suggested GRNs matching this invoice ({{ suggestedBatches.length }})</span>
						<button mat-stroked-button color="primary" type="button" (click)="addAllSuggestedBatches()">Add all</button>
					</div>
					<div class="ai-suggest-list">
						<div class="ai-suggest-chip" *ngFor="let b of suggestedBatches">
							<div class="ai-suggest-chip__text">
								<span>GRN#: {{ b.grnbatch }}<span *ngIf="b.orderno"> · PO#: {{ b.orderno }}</span><span *ngIf="b.sales_orderno"> · SO#: {{ b.sales_orderno }}</span></span>
								<small>matches {{ b.matchCount }} item(s): {{ b.matchedItems.join(', ') }}</small>
							</div>
							<button mat-flat-button color="primary" type="button" (click)="addSuggestedBatch(b)"><mat-icon>add</mat-icon> Add</button>
						</div>
					</div>
				</div>
			</div>

			<!-- Extracted line items (reference; matched against open GRNs) -->
			<div class="row mb-3" *ngIf="extracted && extractedLineStatus.length">
				<div class="col-12">
					<details class="ai-lines">
						<summary>Invoice line items ({{ extractedLineStatus.length }})</summary>
						<table class="table table-sm">
							<thead><tr><th>Item</th><th>Description</th><th class="text-center">Qty</th><th class="text-right">Unit</th><th class="text-right">Ext</th><th class="text-center">GRN</th></tr></thead>
							<tbody>
								<tr *ngFor="let l of extractedLineStatus">
									<td>{{ l.itemcode || '—' }}</td>
									<td>{{ l.description }}</td>
									<td class="text-center">{{ l.qty }}</td>
									<td class="text-right">{{ l.unit_price | currency }}</td>
									<td class="text-right">{{ l.extended | currency }}</td>
									<td class="text-center"><span class="ai-line-badge" [ngClass]="l.matched ? 'ai-line-badge--ok' : 'ai-line-badge--none'">{{ l.matched ? 'open GRN' : 'no GRN' }}</span></td>
								</tr>
							</tbody>
						</table>
					</details>
				</div>
			</div>

			<!-- Invoice Details Section -->
			<div class="row">
				<div class="col-12">
					<h4>Invoice Details</h4>
				</div>
				<div class="col-12 col-md-3">
					<mat-form-field appearance="outline" class="full-width">
						<mat-label>Reference</mat-label>
						<input matInput required formControlName="reference" placeholder="Enter invoice reference" (keyup)="onReferenceBlur()">
						<mat-error *ngIf="invoiceForm.get('reference')?.hasError('referenceUsed')">Invoice reference already used for this vendor</mat-error>
						<mat-error *ngIf="invoiceForm.get('reference')?.invalid && invoiceForm.get('reference')?.errors?.required && invoiceForm.get('reference')?.touched">Reference is required</mat-error>
					</mat-form-field>
				</div>
				<div class="col-12 col-md-3">
					<mat-form-field appearance="outline" class="full-width">
						<mat-label>Department</mat-label>
						<mat-select formControlName="tag">
							<mat-option [value]="null">— None —</mat-option>
							<mat-option *ngFor="let tag of glTags" [value]="tag">{{ tag.description }}</mat-option>
						</mat-select>
					</mat-form-field>
				</div>
				<div class="col-12 col-md-2">
					<mat-form-field appearance="outline" class="full-width">
						<mat-label>Delivery Date</mat-label>
						<input matInput [matDatepicker]="invoiceDate" formControlName="invoiceDate" placeholder="Select delivery date">
						<mat-datepicker-toggle matSuffix [for]="invoiceDate"></mat-datepicker-toggle>
						<mat-datepicker #invoiceDate></mat-datepicker>
					</mat-form-field>
				</div>
				<div class="col-12 col-md-2">
					<mat-form-field appearance="outline" class="full-width">
						<mat-label>Currency</mat-label>
						<mat-select formControlName="currcode">
							<mat-option *ngFor="let cur of currencies" [value]="cur.currabrev">{{ cur.currabrev }} – {{ cur.currency }}</mat-option>
						</mat-select>
					</mat-form-field>
				</div>
				<div class="col-12 col-md-2">
					<mat-form-field appearance="outline" class="full-width">
						<mat-label>Exchange Rate</mat-label>
						<input matInput required formControlName="rate" placeholder="e.g. 100" appSelectOnFocus>
						<mat-hint>100 = 1:1</mat-hint>
					</mat-form-field>
				</div>
			</div>


			<!-- Payment Terms Section -->
			<div class="row mt-3">
				<div class="col-12">
					<h4>Payment Terms</h4>
				</div>
				<div class="col-md-4">
					<mat-form-field appearance="outline" class="full-width">
						<mat-label>Due Date</mat-label>
						<input matInput [matDatepicker]="dueDate" formControlName="dueDate" placeholder="Select due date">
						<mat-datepicker-toggle matSuffix [for]="dueDate"></mat-datepicker-toggle>
						<mat-datepicker #dueDate></mat-datepicker>
					</mat-form-field>
				</div>
				<div class="col-md-4">
					<mat-form-field appearance="outline" class="full-width">
						<mat-label>Discount Date</mat-label>
						<input matInput [matDatepicker]="discountDate" formControlName="discountDate" placeholder="Select discount date">
						<mat-datepicker-toggle matSuffix [for]="discountDate"></mat-datepicker-toggle>
						<mat-datepicker #discountDate></mat-datepicker>
					</mat-form-field>
				</div>
				<div class="col-md-4">
					<mat-form-field appearance="outline" class="full-width">
						<mat-label>Discount Amount</mat-label>
						<span matPrefix>$</span>
						<input matInput formControlName="discountAmount" placeholder="Enter discount amount" (input)="setDiscount($event)">
						<mat-error *ngIf="invoiceForm.get('discountAmount').invalid">Invalid Discount</mat-error>
					</mat-form-field>
					<button type="button" mat-stroked-button color="primary" *ngIf="canDistributeDiscount()"
						(click)="distributeDiscountToLines()"
						matTooltip="Spread this discount evenly per unit across the line items below instead of a lump sum — keeps the total the same but fixes each line's unit cost and PO match.">
						Apply discount to line items
					</button>
				</div>
			</div>
		</div>

		<div class="row">
			<div class="col-md-6 row justify-content-around">
				<div class="d-flex align-items-center mb-2" *ngIf="config.env.package == 'tires'">
					<button mat-raised-button color="accent" (click)="openNoPoItemsModal()" class="mr-2">
						<mat-icon>inventory</mat-icon>
						NO PO Items
					</button>
				</div>
				<mat-form-field class="full-width d-flex" [ngClass]="{'w-75': config?.env?.package == 'tires', 'pl-rem3 w-100': config?.env?.package != 'tires'}" appearance="outline">
					<mat-label>GRN's</mat-label>
					<mat-chip-list #grnList aria-label="Selected Pos">
						<mat-chip *ngFor="let grn of selected_grnbatches" (removed)="remove(grn)">
							GRN#: {{ grn.grnbatch }}
							<a (click)="navigateToPO(grn.orderno); $event.stopPropagation()"
							   *ngIf="grn.orderno"
							   class="po-link"
							   matTooltip="Edit Purchase Order {{grn.orderno}}">
								PO#:{{grn.orderno}}
							</a>
							<span *ngIf="!grn.orderno">PO#:{{grn.orderno}}</span>
							<span *ngIf="grn.sales_orderno"> SO#:{{grn.sales_orderno}}</span>
							<button matChipRemove>
								<mat-icon>cancel</mat-icon>
							</button>
						</mat-chip>
						<input type="text" matInput #grnInput placeholder="Select Purchase Order" [matChipInputFor]="grnList" [matChipInputSeparatorKeyCodes]="separatorKeysCodes" (matChipInputTokenEnd)="add($event)" formControlName="grns" [matAutocomplete]="auto">
					</mat-chip-list>

					<mat-autocomplete #auto="matAutocomplete" (optionSelected)="selected($event)">
						<mat-option *ngFor="let grn of filteredGrnBatches()" [value]="grn">
							GRN#: {{ grn.grnbatch }} PO#:{{grn.orderno}}<span *ngIf="grn.sales_orderno"> SO#:{{grn.sales_orderno}}</span>
						</mat-option>
					</mat-autocomplete>
					<mat-suffix class="grn-count-label">GRN COUNT
						<ng-container *ngIf="getting_supplier">
							<svg class="spinner" width="18px" height="18px" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
								<circle class="path" color="white" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30"></circle>
							</svg>
						</ng-container>
						<ng-container *ngIf="!getting_supplier">{{filteredGrnBatches().length}}</ng-container>

					</mat-suffix>
				</mat-form-field>
			</div>

			<div class="col-md-6" [ngClass]="selected_grns.length >= 1 ? 'mt-3' : '' ">
				<app-gl-account-lookup (glselected)="addGl($event)" [reset]="resetGlLookup"></app-gl-account-lookup>
			</div>
			<div class="col-md-12">
				<div class="table invoice-table">
					<table class="table table-sm table-fixed" *ngIf="selected_grns.length || glcharges.length">
						<thead>
							<tr>
								<th>Item</th>
								<th>Desc</th>
								<th>PO#</th>
								<th matTooltip="Received qty / PO ordered qty">Recvd / Ord</th>
								<th class="text-center" matTooltip="Qty already invoiced on prior vendor invoices / qty received on this GRN line">Invcd / Recd</th>
								<th class="text-right">PO Price</th>
								<th class="text-center">Qty</th>
								<th class="text-right">Invoice Price</th>
								<th class="text-right" *ngIf="config?.env?.package == 'tires'">FET</th>
								<th class="text-right">{{ config?.env?.package == 'tires' ? 'Line Subtotal' : 'Line Total' }}</th>
								<th class="text-right"></th>
							</tr>
						</thead>
						<tbody>
							<ng-container *ngFor="let grn of selected_grns">
							<tr>
								<td class="pt-4">
									{{ grn.itemcode }}
								</td>
								<td class="pt-4">
									{{ grn.itemdescription }}
									<br/>
									<button mat-stroked-button [ngClass]="grn?.showadditional ? 'grn-remove-note' : 'grn-additional-note'" (click)="showNoteField(grn)">
										<ng-container *ngIf="!grn?.showadditional">+ note</ng-container>
										<ng-container *ngIf="grn?.showadditional">- note</ng-container>
									</button>
									<mat-form-field appearance="outline" subscriptSizing="dynamic" class="vendor-line-dept text-left mt-2" *ngIf="glTags?.length">
										<mat-label>Department</mat-label>
										<mat-select [value]="grn.tag ?? null" (selectionChange)="setGrnDept($event, grn)">
											<mat-option [value]="null">— Header default —</mat-option>
											<mat-option *ngFor="let tag of glTags" [value]="tag">{{ tag.description }}</mat-option>
										</mat-select>
									</mat-form-field>
								</td>
								<td class="pt-4">
									<a (click)="navigateToPO(grn.orderno)"
									   *ngIf="grn.orderno"
									   class="po-link"
									   matTooltip="Edit Purchase Order {{grn.orderno}}">
										{{ grn.orderno }}
									</a>
									<span *ngIf="!grn.orderno">-</span>
								</td>
								<td class="pt-4 text-center">
									<span class="vendor-qty-pill" [ngClass]="vendorRecvdOrdPillClass(grn)" matTooltip="Received / PO ordered">{{ grn.qtyrecd }} / {{ grn.quantityord }}</span>
								</td>
								<td class="pt-4 text-center">
									<span class="vendor-qty-pill" [ngClass]="vendorInvcdRecdPillClass(grn)" matTooltip="Already invoiced / received">{{ (+grn.quantityinv || 0) }} / {{ (+grn.qtyrecd || 0) }}</span>
								</td>
								<td class="pt-4 text-right">{{ (grn.poUnitPrice != null && grn.poUnitPrice !== '' ? grn.poUnitPrice : grn.unitprice) | currency}}</td>
								<td class="text-center">
									<mat-form-field appearance="outline" class="text-center">
										<mat-label>Invoice Qty.</mat-label>
										<input matInput required [(value)]="grn.toinvoice" (input)="setQty($event, grn)" appSelectOnFocus>
									</mat-form-field>
								</td>
								<td class="text-right align-top vendor-invoice-price-cell">
									<div class="vendor-price-field-shell"
										[ngClass]="{
											'vendor-price-field-shell--warn': invoicePriceDiffersFromPo(grn),
											'vendor-price-field-shell--ok': invoicePriceMatchesPo(grn)
										}"
										[matTooltip]="invoicePriceDiffersFromPo(grn) ? invoicePricePoDiffTooltip(grn) : invoicePriceMatchTooltip(grn)"
										[matTooltipDisabled]="!invoicePriceDiffersFromPo(grn) && !invoicePriceMatchesPo(grn)">
										<mat-form-field appearance="outline" class="vendor-price-mat-field text-left" subscriptSizing="dynamic">
											<span matPrefix>$</span>
											<mat-label>Invoice Price</mat-label>
											<input matInput required [(value)]="grn.unitprice" (input)="setPrice($event, grn)" moneyPipe appSelectOnFocus>
											<mat-hint *ngIf="invoicePriceMatchesPo(grn)" class="vendor-price-hint vendor-price-hint--ok">Matches PO</mat-hint>
											<mat-hint *ngIf="invoicePriceDiffersFromPo(grn)" class="vendor-price-hint vendor-price-hint--warn">Differs from PO</mat-hint>
										</mat-form-field>
									</div>
								</td>
								<td class="pt-4 text-right" *ngIf="config?.env?.package == 'tires'">
									{{ ((grn.fetcharge || 0) * (grn.toinvoice || 0)) | currency}}
								</td>
								<td class="pt-4 text-right">
									<b> {{ ((grn.unitprice || 0) * (grn.toinvoice || 0)) | currency}}</b>
								</td>
								<td class="pt-4 text-right">
									<button mat-flat-button (click)="removeLine(grn)">
										<i class="material-icons">remove</i>
									</button>
								</td>								
							</tr>
							<tr *ngIf="grn?.showadditional">
								<td [attr.colspan]="config?.env?.package == 'tires' ? 11 : 10" class="pt-4 text-center">
									<mat-form-field appearance="outline" class="text-left w-50">
										<mat-label>{{grn.itemcode}} note</mat-label>
										<input matInput [(value)]="grn.additionaldesc" (input)="setNote($event, grn)">
									</mat-form-field>
								</td>	
							</tr>
							</ng-container>
							<ng-container *ngFor="let gl of glcharges">
								<tr>
									<td class="pt-4">{{ gl.account }}</td>
									<td class="pt-4">{{ gl.name }}</td>
									<td></td>
									<td></td>
									<td></td>
									<td></td>
									<td class="text-right">
										<mat-form-field appearance="outline" class="text-left">
											<span matPrefix>$</span>
											<mat-label>Price</mat-label>
											<input matInput required [(value)]="gl.value" (input)="setGlPrice($event, gl)" moneyPipe appSelectOnFocus>
										</mat-form-field>
									</td>
									<td class="pt-4"></td>
									<td class="pt-4" *ngIf="config?.env?.package == 'tires'"></td>
									<td class="pt-4 text-right">
										<b> {{ gl.value * gl.quantity | currency}}</b>
									</td>
									<td class="pt-4 text-right">
										<button mat-flat-button (click)="removeGl(gl)">
											<i class="material-icons">remove</i>
										</button>
									</td>
								</tr>
								<tr>
									<td [attr.colspan]="config?.env?.package == 'tires' ? 11 : 10" class="pt-0">
										<div class="vendor-line-extra">
											<mat-form-field appearance="outline" subscriptSizing="dynamic" class="vendor-line-note">
												<mat-label>{{gl.account}} note</mat-label>
												<input matInput [(value)]="gl.description" (input)="setGlNote($event, gl)">
											</mat-form-field>
											<mat-form-field appearance="outline" subscriptSizing="dynamic" class="vendor-line-dept" *ngIf="glTags?.length">
												<mat-label>Department</mat-label>
												<mat-select [value]="gl.tag ?? null" (selectionChange)="setGlDept($event, gl)">
													<mat-option [value]="null">— Header default —</mat-option>
													<mat-option *ngFor="let tag of glTags" [value]="tag">{{ tag.description }}</mat-option>
												</mat-select>
											</mat-form-field>
										</div>
									</td>
								</tr>
							</ng-container>
						</tbody>
					</table>
				</div>
				<div class="row">
					<div class="col-12 text-right" *ngIf="selected_grns.length || glcharges.length">
						<h5 class="mr-4">{{ config?.env?.package == 'tires' ? 'Subtotal (before FET)' : 'Sub-Total' }}: {{ invoiceTotal | currency }}</h5>
						<ng-container>
							<div class="ml-auto text-right" *ngFor="let tax of applicableTaxes">
								<mat-form-field appearance="fill" class="ml-auto col-2 text-right">
									<span matPrefix>$</span>
									<mat-label>{{ tax.description }}</mat-label>
									<input matInput required value="{{tax.taxamount | number:'1.2-2'}}" (input)="setTaxValue($event, tax)" appSelectOnFocus>
								</mat-form-field>
							</div>
						</ng-container>
					</div>
					<div class="col-12 text-right">

						<h5 class="mr-4">Tax: {{ taxTotal | currency }}</h5>
						<h5 class="mr-4" *ngIf="config?.env?.package == 'tires' && fetTotal > 0">FET Total: {{ fetTotal | currency }}</h5>
						<h5 class="mr-4">Discount: {{ discount | currency }}</h5>
						<h5 class="mr-4">Total: {{ totalTotal | currency }}</h5>
						<ng-container *ngIf="sending; else submitButton">
							<button mat-raised-button class="loading-button" disabled>
								<svg class="cool-spinner" viewBox="0 0 50 50">
									<circle class="circle" cx="25" cy="25" r="20"></circle>
								</svg>

							</button>
						</ng-container>

						<ng-template #submitButton>
							<button mat-stroked-button type="button" color="primary" class="mr-2" (click)="openGlPreview()">
								Preview GL
							</button>
							<button mat-raised-button [color]="invoiceForm.valid ? 'primary' : 'accent'" [disabled]="invoiceForm.invalid" (click)="onSubmit()">
								Create Invoice
							</button>
						</ng-template>

					</div>
				</div>
			</div>
		</div>
		<!-- Documents & Uploads: read-PDF autofill, attachments, bulk CSV/Excel — grouped, collapsed by default -->
		<mat-expansion-panel class="vendor-documents-panel mt-4">
			<mat-expansion-panel-header>
				<mat-panel-title><mat-icon>folder_open</mat-icon> Documents &amp; Uploads</mat-panel-title>
				<mat-panel-description>Read a PDF to auto-fill, attach files, or bulk-upload invoices</mat-panel-description>
			</mat-expansion-panel-header>
			<div class="row vendor-documents">
				<div class="col-12 col-lg-6 vendor-documents__col">
					<span class="vendor-documents__label">Read Invoice PDF <small>— auto-fills the fields above</small></span>
					<ngx-dropzone (change)="onInvoicePdfSelected($event)" [disabled]="extracting" accept="application/pdf,.pdf" class="vendor-dropzone">
						<ngx-dropzone-label>
							<ng-container *ngIf="!extracting"><mat-icon>auto_awesome</mat-icon><span>Drop invoice PDF to auto-fill</span></ng-container>
							<ng-container *ngIf="extracting"><span>Reading invoice…</span></ng-container>
						</ngx-dropzone-label>
					</ngx-dropzone>
					<div class="ai-extract-summary mt-2" *ngIf="extracted">
						<div *ngFor="let w of extractWarnings" class="ai-warn"><mat-icon>warning</mat-icon> {{ w }}</div>
						<span><b>Invoice #</b> {{ extracted.invoice_number || '—' }}</span>
						<span><b>Date</b> {{ extracted.invoice_date || '—' }}</span>
						<span *ngIf="extracted.po_numbers?.length"><b>PO</b> {{ extracted.po_numbers.join(', ') }}</span>
						<span><b>Invoice total</b> {{ extracted.total_amount | currency }}</span>
						<span [ngClass]="extractedTotalMatches() ? 'ai-recon-ok' : 'ai-recon-diff'">
							<b>Entered</b> {{ totalTotal | currency }}
							<ng-container *ngIf="!extractedTotalMatches() && totalTotal > 0"> (differs)</ng-container>
						</span>
					</div>
				</div>
				<div class="col-12 col-lg-6 vendor-documents__col">
					<span class="vendor-documents__label">Attachments <small>— PDF, images, Word, Excel</small></span>
					<ngx-dropzone (change)="onAttachmentSelect($event)" class="vendor-dropzone">
						<ngx-dropzone-label><mat-icon>attach_file</mat-icon><span>Drop or click to attach</span></ngx-dropzone-label>
						<ngx-dropzone-preview *ngFor="let f of attachmentFiles" [removable]="true" (removed)="onAttachmentRemove(f)">
							<ngx-dropzone-label>{{ f.name }} ({{ f.type }})</ngx-dropzone-label>
						</ngx-dropzone-preview>
					</ngx-dropzone>
				</div>
				<div class="col-12 vendor-documents__col vendor-documents__col--full">
					<span class="vendor-documents__label">Bulk Upload <small>— CSV/Excel, many invoices</small></span>
					<app-vendor-invoice-upload [vendor]="vendor_data" (newvendortrans)="newvendortrans.emit($event)" class="w-100"></app-vendor-invoice-upload>
				</div>
			</div>
		</mat-expansion-panel>
	</form>
	<div class="text-center bold" *ngIf="!selected_grns.length  && !glcharges.length">
		Add Purchase Orders And/Or GL Charges
	</div>
</ng-container>